import React from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useFirebase } from '../contexts/FirebaseContext';
import { ShieldAlert, CreditCard, ChevronRight, Loader2, Clock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
  featureName: string;
  featureId?: import('../hooks/useSubscription').AppFeature;
}

export const SubscriptionGate: React.FC<Props> = ({ children, featureName, featureId }) => {
  const { tenantId } = useFirebase();
  const sub = useSubscription(tenantId);

  if (sub.loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <p className="text-gray-500 font-medium">Verifying access...</p>
      </div>
    );
  }

  // Check if trial/subscription is active first
  const isAccountActive = sub.isActive || (sub.isTrialing && !sub.isExpired);
  
  // Check if feature is allowed for the current plan
  const isFeatureAllowed = featureId ? sub.hasFeature(featureId) : true;

  if (isAccountActive && isFeatureAllowed) {
    return (
      <div className="space-y-6">
        {sub.isTrialing && (
          <div className="bg-blue-600 p-3 rounded-2xl flex items-center justify-between text-white shadow-xl shadow-blue-500/20 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-xl">
                <Clock size={16} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Trial Active</p>
                <p className="text-sm font-bold">{sub.trialDaysLeft} days remaining in your business trial.</p>
              </div>
            </div>
            <Link 
              to="/settings" 
              className="px-4 py-2 bg-white text-blue-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-50 transition-all flex items-center gap-2"
            >
              Upgrade Plan
              <ChevronRight size={14} />
            </Link>
          </div>
        )}
        {children}
      </div>
    );
  }

  // Handle plan upgrade requirement (Inline Mode)
  if (isAccountActive && !isFeatureAllowed) {
    return (
      <div className="space-y-6 animate-in fade-in duration-200">
        <div className="bg-gradient-to-r from-indigo-900 to-indigo-950 border border-indigo-800 p-5 rounded-[2rem] flex flex-col md:flex-row md:items-center justify-between text-white shadow-xl gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-500/15 text-indigo-400 rounded-2xl flex items-center justify-center shrink-0 border border-indigo-500/10">
              <Sparkles size={22} className="animate-pulse" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Growth Feature • Premium Preview</p>
              <h4 className="text-sm font-black uppercase italic tracking-tight">{featureName} Tier Status</h4>
              <p className="text-xs text-slate-300 font-medium font-sans">Compare live operational metrics, run custom compliance audits, and unlock multi-location indexes by upgrading to the Growth plan.</p>
            </div>
          </div>
          <Link 
            to="/settings" 
            className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all inline-flex items-center gap-1.5 self-start md:self-auto shadow-lg shadow-indigo-600/20 border border-indigo-500"
          >
            Upgrade to Growth
            <ChevronRight size={12} />
          </Link>
        </div>
        {children}
      </div>
    );
  }

  // Inactive Account/Expired Trial (Inline Mode)
  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      <div className="bg-gradient-to-r from-slate-900 to-stone-900 border border-slate-800 p-5 rounded-[2rem] flex flex-col md:flex-row md:items-center justify-between text-white shadow-xl gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-rose-500/15 text-red-400 rounded-2xl flex items-center justify-center shrink-0 border border-rose-500/10">
            <CreditCard size={22} className="text-red-400" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-400">Trial Period Terminated • View Only</p>
            <h4 className="text-sm font-black uppercase italic tracking-tight">{featureName} View</h4>
            <p className="text-xs text-slate-300 font-medium font-sans">Your sandbox evaluation has concluded. Standard features remain fully visible. Activate your subscription to resume automated exports.</p>
          </div>
        </div>
        <Link 
          to="/settings" 
          className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all inline-flex items-center gap-1.5 self-start md:self-auto shadow-lg shadow-indigo-600/20 border border-indigo-500"
        >
          Activate Account
          <ChevronRight size={12} />
        </Link>
      </div>
      {children}
    </div>
  );
};
